import React, { useState, useRef, useEffect } from 'react';
import { PrimaryButton, SecondaryButton } from '../../../components/Buttons';
import { ChevronDown, FileText, ImageIcon, TextCursorInput, Video, XCircle, ArrowLeft } from 'lucide-react';
import { useLocation, useParams } from 'react-router-dom';
import { useUser } from '../../../context/UserContext';
import axiosInstance from '../../../services/api/axios';
import TemplateForm from './TemplateForm';
import ConditionBuilder from './ConditionBuilder';
import { toast } from 'react-toastify';

// In Template.jsx

const formatWhatsappText = (text) => {
  if (!text) return '';
  
  let formattedText = text;

  // 1. Convert formatting tags to WhatsApp markdown
  formattedText = formattedText.replace(/<strong\s*?>(.*?)<\/strong>/gi, '*$1*');
  formattedText = formattedText.replace(/<b\s*?>(.*?)<\/b>/gi, '*$1*');
  formattedText = formattedText.replace(/<em\s*?>(.*?)<\/em>/gi, '_$1_');
  formattedText = formattedText.replace(/<i\s*?>(.*?)<\/i>/gi, '_$1_');
  formattedText = formattedText.replace(/<strike\s*?>(.*?)<\/strike>/gi, '~$1~');
  formattedText = formattedText.replace(/<del\s*?>(.*?)<\/del>/gi, '~$1~');
  formattedText = formattedText.replace(/<code\s*?>(.*?)<\/code>/gi, '`$1`');

  // 2. Convert all HTML line and paragraph breaks into a standard newline character.
  // This is a crucial step to standardize the input before cleaning it up.
  formattedText = formattedText.replace(/<br\s*?\/?>/gi, '\n');
  formattedText = formattedText.replace(/<\/p>/gi, '\n');
  formattedText = formattedText.replace(/<\/div>/gi, '\n');
  
  // 3. Strip away any remaining HTML tags (like <p>, <div>, etc.).
  formattedText = formattedText.replace(/<[^>]+>/g, '');
  
  // 4. Decode HTML space entities. This is vital for preserving multiple spaces.
  formattedText = formattedText.replace(/&nbsp;/g, ' ');
  // Decode other common entities
  formattedText = formattedText.replace(/&amp;/g, '&');
  formattedText = formattedText.replace(/&lt;/g, '<');
  formattedText = formattedText.replace(/&gt;/g, '>');

  // 5. CRUCIAL CLEANUP: Normalize the newlines.
  // Now that all breaks are `\n`, we can manage the spacing precisely.
  // Replace three or more newlines with just two. This creates a clean paragraph break 
  // from messy HTML (like </p><p><br></p>) but prevents excessive blank lines.
  formattedText = formattedText.replace(/\n{3,}/g, '\n\n'); // <-- THIS IS A KEY PART OF THE FIX

  // 6. Trim whitespace ONLY from the start and end of the entire string.
  return formattedText.trim();
};

// Function to check if leadType is condition-related
const isConditionLeadType = (leadType, hasConditions = false) => {
  const conditionTypes = ['conditional', 'campaign_conditional'];
  return (leadType && conditionTypes.includes(leadType.toLowerCase())) || hasConditions;
};

function Template({ initialData, onChange, onUpdate, onClose, onDelete, nodeId, leadType: passedLeadType, isViewingSavedTemplate = false, templateId = null, isUpdatingTemplate = false, isPublishedTemplate = false }) {
 
  console.log("----", initialData)
  const [conditionString, setConditionString] = useState(initialData?.conditionString || '');
  const [templateName, setTemplateName] = useState(initialData?.templateName || '');
  const [templateType, setTemplateType] = useState(initialData?.templateType || '');
  const [headerType, setHeaderType] = useState(initialData?.headerType || 'text');
  const [sendDate, setSendDate] = useState(initialData?.sendDatetime || '');
  const [headerText, setHeaderText] = useState(initialData?.headerText || '');
  const [headerMedia, setHeaderMedia] = useState(initialData?.headerMedia || null);
  const [body, setBody] = useState(initialData?.body || '');
  const [buttons, setButtons] = useState(initialData?.buttons || []);
  const [leadType, setLeadType] = useState(initialData?.leadType || []);
  const [dummyValues, setDummyValues] = useState(initialData?.dummyValues || {});
  const [conditions, setConditions] = useState(initialData?.conditions || []);
  const [propertyConfigIds, setPropertyConfigIds] = useState(initialData?.propertyConfigIds || []);
  const { userInfo } = useUser();
  const [activeStep, setActiveStep] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);

  const location = useLocation();
  const { project } = location.state || {};
 
  const { leadType: urlLeadType } = useParams();

  // Check if this is a reply node
  const isReplyNode = nodeId && nodeId.startsWith('reply-');
  const isInitialNode = !isReplyNode;

  // Check if this is a condition-based template based on either passed leadType or URL leadType
  // Only show conditions for initial nodes (not reply nodes)
  const currentLeadType = passedLeadType || urlLeadType;
  const needsConditions = !isReplyNode && (isConditionLeadType(currentLeadType, !!conditionString));

  const quillRef = useRef(null);

  // Load the selected image when the initialData changes
  useEffect(() => {
    // Set loading flag to prevent onChange from formatting during initial load
    setIsLoadingInitialData(true);
    
    if (initialData?.headerMedia?.base64) {
      setSelectedImage(initialData.headerMedia.base64);
      // Also set the headerMedia state with both base64 and file if available
      setHeaderMedia({
        base64: initialData.headerMedia.base64,
        file: initialData.headerMedia.file // This will be reconstructed from localStorage
      });
    } else if (initialData?.media?.content) {
      // Handle API format media
      setSelectedImage(initialData.media.content);
    } else {
      setSelectedImage(null); // Clear the selected image if no base64 data
      setHeaderMedia(null);
    }
    
    // Initialize conditions from initialData if available
    if (initialData?.conditions) {
      setConditions(initialData.conditions);
    }
    
    if (initialData?.conditionString) {
      setConditionString(initialData.conditionString);
    }

    // Update templateType when initialData changes
    if (initialData?.templateType) {
      setTemplateType(initialData.templateType);
    }

    // Update sendDate when initialData changes
    if (initialData?.sendDatetime) {
      setSendDate(initialData.sendDatetime);
    }
    
    // Reset loading flag after a short delay to allow state updates to complete
    setTimeout(() => {
      setIsLoadingInitialData(false);
    }, 100);
  }, [initialData]);

  // Call onChange whenever form data changes
  useEffect(() => {
    // Don't trigger onChange when initially loading the form or when loading initial data
    if (!templateName && !headerText && !body) {
      return;
    }

    // Don't trigger onChange when loading initial data to prevent formatting
    if (isLoadingInitialData) {
      return;
    }

    if (onChange) {
      const headerMediaToSave = headerMedia?.base64
        ? { 
            base64: headerMedia.base64,
            file: headerMedia.file // Also pass the file object
          }
        : null;

      // For onChange, we want to pass the original HTML body to preserve formatting in the editor
      // The preview component will handle the formatting for display
      const templateData = {
        templateName,
        headerType,
        headerText,
        headerMedia: headerMediaToSave,
        body: body, // Pass the original HTML body, not formatted
        buttons,
        leadType,
        variable: Object.keys(dummyValues).join(', '),
        dummyValues,
        conditions,
        conditionString,
        propertyConfigIds,
        customerWhatsappTemplateId: templateId || initialData?.customerWhatsappTemplateId,
        sendDatetime: sendDate
      };
      
      
      
      onChange(templateData); // Call the onChange to make live
    }
  }, [templateName, headerType, headerText, headerMedia, body, buttons, leadType, dummyValues, conditions, conditionString, propertyConfigIds, onChange, templateId, initialData, sendDate, isLoadingInitialData]);

  const handleDummyValueChange = (variableName, value) => {
    setDummyValues((prev) => ({ ...prev, [variableName]: value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setSelectedImage(base64String); // set the image as base64 for preview
        setHeaderMedia({ 
          base64: base64String,
          file: file // Store the original file object for upload
        }); // Update headerMedia with both base64 and file
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedImage(null);
      setHeaderMedia(null);
    }
  };

  const handleUpdate = (templateData) => {
 

    const headerMediaToSave = headerMedia?.base64
        ? { 
            base64: headerMedia.base64,
            file: headerMedia.file // Also pass the file object
          }
        : null;

    // Format the body text according to WhatsApp guidelines only when saving
    const formattedBody = formatWhatsappText(templateData.body || body);

    const updatedTemplateData = {
        ...templateData,
        body: formattedBody, // Save the formatted version
        headerMedia: headerMediaToSave,
        templateType: templateType,
        sendDatetime: sendDate,
        isUpdating: true,
        customerWhatsappTemplateId: templateId || initialData?.customerWhatsappTemplateId
    };
    
  

    if (onUpdate) {
        onUpdate(updatedTemplateData);
        onClose(); // Close the drawer after saving
    } else {
        console.error('Parent onUpdate is not defined');
        toast.error('Failed to save template');
    }
  };

  const resetForm = () => {
    setTemplateName('');
    setHeaderType('text');
    setHeaderText('');
    setHeaderMedia(null);
    setBody('');
    setButtons([]);
    setLeadType([]);
    setDummyValues({});
    setConditions([]);
    setConditionString('');
    setPropertyConfigIds([]);
    setIsOpen(false);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null); // Clear the selected image
    setHeaderMedia(null);
  };

  const moveToNextStep = () => {
    setActiveStep(1);
  };

  const moveToPreviousStep = () => {
    setActiveStep(0);
  };

  const renderStepIndicator = () => {
    // Don't show step indicator for reply nodes
    if (!needsConditions || isReplyNode) return null;
    
    return (
      <div className="mb-6">
        <div className="flex items-center justify-center">
          <div 
            className={`flex items-center ${activeStep === 0 ? 'text-flamingo-500' : 'text-gray-600'} cursor-pointer`}
            onClick={() => setActiveStep(0)}
          >
            <div className={`rounded-full h-8 w-8 flex items-center justify-center border-2 ${activeStep === 0 ? 'border-flamingo-500 bg-flamingo-100' : 'border-gray-300'}`}>
              1
            </div>
            <span className="ml-2 font-medium">Conditions</span>
          </div>
          <div className={`w-12 h-1 mx-4 ${activeStep === 1 ? 'bg-flamingo-500' : 'bg-gray-300'}`}></div>
          <div 
            className={`flex items-center ${activeStep === 1 ? 'text-flamingo-500' : 'text-gray-600'} cursor-pointer`}
            onClick={() => setActiveStep(1)}
          >
            <div className={`rounded-full h-8 w-8 flex items-center justify-center border-2 ${activeStep === 1 ? 'border-blue-600 bg-blue-100' : 'border-gray-300'}`}>
              2
            </div>
            <span className="ml-2 font-medium">Template</span>
          </div>
        </div>
      </div>
    );
  };

  const renderFormContent = () => {
    // Don't show condition builder for reply nodes
    if (needsConditions && activeStep === 0 && !isReplyNode) {
      return (
        <div className='bg-white rounded-md p-4 mb-4'>
          <h2 className="text-xl font-semibold mb-4">Set Campaign Conditions</h2>
          <p className="text-sm text-gray-600 mb-4">
            Define conditions that must be met for this template to be sent to a lead.
          </p>
          
          <ConditionBuilder 
            setConditionString={(conditionString, conditions) => {
              setConditionString(conditionString);
              if (conditions) setConditions(conditions);
            }} 
            initialConditions={conditions}
          />
          
          <div className="flex justify-end mt-24">
            <div className="flex gap-4">
             
              <PrimaryButton onClick={moveToNextStep}>
                Next
              </PrimaryButton>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <>
        {needsConditions && (
          <div className="mb-4">
            <button
              onClick={moveToPreviousStep}
              className="flex items-center text-gray-600 hover:text-gray-800  bg-white rounded-md p-2 border border-gray-200 transition-colors"
            >
              <ArrowLeft size={20} className="mr-1" />
              
            </button>
          </div>
        )}
        <TemplateForm
          templateName={templateName}
          templateType={templateType}
          setTemplateName={setTemplateName}
          setTemplateType={setTemplateType}
          headerType={headerType}
          setHeaderType={setHeaderType}
          headerText={headerText}
          setHeaderText={setHeaderText}
          headerMedia={headerMedia}
          setHeaderMedia={setHeaderMedia}
          body={body}
          setBody={setBody}
          buttons={buttons}
          setButtons={setButtons}
          leadType={leadType}
          setLeadType={setLeadType}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          onTemplateCreate={handleUpdate}
          onUpdate={handleUpdate}
          dummyValues={dummyValues}
          handleDummyValueChange={handleDummyValueChange}
          quillRef={quillRef}
          handleImageChange={handleImageChange}
          selectedImage={selectedImage}
          handleRemoveImage={handleRemoveImage}
          propertyConfigIds={propertyConfigIds}
          setPropertyConfigIds={setPropertyConfigIds}
          onBack={onClose}
          onDelete={onDelete}
          nodeId={nodeId}
          isInitialNode={isInitialNode}
          isViewingSavedTemplate={isViewingSavedTemplate}
          templateId={templateId}
          initialData={initialData}
          sendDate={sendDate}
          setSendDate={setSendDate}
          isUpdatingTemplate={isUpdatingTemplate}
          isPublishedTemplate={isPublishedTemplate}
        />
      </>
    );
  };

  return (
    <div>
      <div className="flex h-full px-2">
        <div className="md:w-3/4 flex-1 flex flex-col order-1">
          {needsConditions && renderStepIndicator()}
          {renderFormContent()}
        </div>
      </div>
    </div>
  );
}

// Helper function to generate components for the request body
const generateComponents = (headerType, headerText, body, buttons, dummyValues) => {
  const components = [];

  if (headerType === 'text' && headerText) {
    components.push({ type: 'HEADER', format: 'TEXT', text: headerText });
  }

  if (body) {
    // Format the body text according to WhatsApp guidelines
    const formattedBody = formatWhatsappText(body);
    const bodyComponent = { type: 'BODY', text: formattedBody };
    const variableMatches = body.match(/{{([^}]+)}}/g);

    if (variableMatches) {
      bodyComponent.example = {
        body_text: [variableMatches.map(match => {
          const varName = match.replace(/[{}]/g, '');
          return dummyValues[varName] || '';
        })]
      };
    }
    components.push(bodyComponent);
  }

  if (buttons.length > 0) {
    components.push({
      type: 'BUTTONS',
      buttons: buttons.map(button => ({
        type: button.type || 'QUICK_REPLY',
        text: button.label || button.buttonText
      }))
    });
  }

  return components;
};

export default Template;